<template>
    <div>
        <div v-if="linkedinLoginStatus.loggedIn">
            <div v-if="linkedinProfiles.length">
                <p>
                    These are the LinkedIn profiles that you are an admin of. Click on a profile's row to view the accounts you can add it to.
                </p>

                <search-input
                    v-if="linkedinProfiles.length > 5"
                    class="linkedin-auth__search-bar"
                    autofocus
                    placeholder="Search for a profile"
                    v-model="searchTerm">
                </search-input>
                <div class="linkedin-auth__profiles dark-scrollbars dark-scrollbars--visible"
                     :style="linkedinProfiles.length > 5 ? 'height: clamp(230px, 22vh, 600px)' : 'height: clamp(250px, 26vh, 600px)'">

                    <online-profile-row v-for="profile in filteredProfiles"
                                        :profile="profile"
                                        :linked-profile="profile.linkedProfile"
                                        :key="profile.handleId"
                                        :expand-row="showAccounts"
                                        :reauthorised-profiles="reauthorisedProfiles"
                                        :profile-just-reauthorised="profileJustReauthorised"
                                        @collapse-row="showAccounts = false"/>
                    <div style="margin-top: 10px" v-if="!filteredProfiles.length">
                        No profiles match your search
                    </div>
                </div>
            </div>
            <div v-else>
                LinkedIn company profiles that you are an admin of will be shown here. You are currently not an admin of any LinkedIn company profiles.
            </div>
        </div>
        <div v-else>
            <p v-if="linkedinLoginStatus.loginInvalidated">
                You have logged in through LinkedIn before, however the login has been invalidated.
                Please login again so that LinkedIn company profiles you have previously authorised can continue to collect data.
            </p>
            <p v-else :class="{'failed-login': linkedinLoginFailed}">
                {{ loginMessage }}
            </p>
        </div>

        <div class="linkedin-auth__btn-bar">
            <be-button class="btn-linkedin" @click="loginThroughLinkedIn">
                {{ loginBtnText }}
            </be-button>

            <div class="login-status" v-if="linkedInLoggedInAs">
                <div>Logged in through <i class="symbol-linkedin-rect"></i>LinkedIn as {{ linkedInLoggedInAs.name }}</div>
                <div v-if="linkedInLoggedInAs.expire">Login expires on {{ formatDate(linkedInLoggedInAs.expire, 'DD MMMM YYYY, HH:mm') }}</div>
            </div>

        </div>
    </div>
</template>

<script>
import {beef} from "@/store/Services";
import SearchInput from "@/components/inputs/SearchInput";
import {notifyWithText, notifyUserOfError} from "@/app/framework/notifications/Notifications";
import {profileTypes} from "@/setup/profiles/ProfileUtils";
import BeButton from "@/components/buttons/BeButton";
import OnlineProfileRow from "@/app/framework/dialogs/user-settings/views/platform-auth/OnlineProfileRow";
import VuexStore from "@/store/vuex/VuexStore";
import {mapActions, mapMutations, mapState} from "vuex";
import {openWindowCenter} from "@/app/framework/dialogs/user-settings/views/platform-auth/PlatformAuthUtils";
import {formatDate} from "@/app/utils/Format";

export default {
    name: "LinkedinAuth",
    store: VuexStore,
    components: {OnlineProfileRow, BeButton, SearchInput},

    props: {
        linkedinProfiles: {
            type: Array,
            required: true
        },
        expandAccounts: {
            type: Boolean,
            default: false
        }
    },

    data: function() {
        return {
            profileTypes: profileTypes,
            linkedinLoginFailed: false,
            loginMessage: "Login with LinkedIn to see your managed company profiles. Once logged in, you can authorise your managed company profiles on DataEQ accounts that you have access to.",
            searchTerm: "",
            showAccounts: false,
            reauthorisedProfiles: new Set(),
            profileJustReauthorised: ""
        }
    },

    computed: {
        ...mapState(['user', 'account']),

        ...mapState('userPlatformAuth', ['linkedInLoggedInAs', 'linkedinLoginStatus']),

        loginBtnText() {
            return this.linkedinLoginStatus.loggedIn ? "Refresh LinkedIn login" : "Login with LinkedIn";
        },

        filteredProfiles() {
            let search = this.searchTerm.toLowerCase();

            let profiles = this.linkedinProfiles.filter(profile => profile.name.toLowerCase().includes(search) ||
                profile.handle.toLowerCase().includes(search) ||  profile.handleId.toLowerCase().includes(search));

            return profiles.sort((a, b) => {
                return a.name > b.name ? 1 : -1
            });
        }
    },

    created() {
        this.showAccounts = this.expandAccounts;
    },

    methods: {
        ...mapActions('userPlatformAuth', [
            'initializeManagedLinkedinProfiles'
        ]),

        ...mapActions('profiles', ['refreshProfiles']),

        ...mapMutations('userPlatformAuth', ['setLinkedinLoginStatus']),

        formatDate,

        async loginThroughLinkedIn() {
            try {
                this.setLoading("Communicating with LinkedIn...", true);

                let response = await beef.get(`/api/online-profile/linkedin/login?accountCode=${this.account.code}`);

                // handle success of callback
                let addedFunctionName = `userPlatformAuth${this.user.id}`;

                window[addedFunctionName] = async (paramsMap) => {
                    if (paramsMap["auth"] === "success") {

                        if (window.location.href.indexOf("setup/profiles") !== -1) {
                            this.refreshProfiles(true);
                        }

                        this.setLinkedinLoginStatus({loggedIn: true, loginInvalidated: false});

                        notifyWithText("LinkedIn login successful.",
                            null,
                            "<i class='symbol-linkedin-rect'></i>");

                        this.setLoading("LinkedIn login successful! Fetching your managed company profiles...", true);

                        this.triggerFetchProfiles();

                        this.showAccounts = true;
                    } else {
                        this.loginMessage = "Login with LinkedIn failed. Please reload the page and try again or contact DataEQ support.";
                        this.linkedinLoginFailed = true;

                        this.setLoading("", false);
                    }
                }

                openWindowCenter(response.data.url, "", 700, 900);
            } catch (e) {
                console.error("Error occurred while trying to log user in with LinkedIn ", e);
                this.loginMessage = "Login with LinkedIn failed. Please reload the page and try again or contact DataEQ support.";
                this.linkedinLoginFailed = true;

                this.setLoading("", false);

                notifyUserOfError("An error occurred while trying to log you in with LinkedIn. Please close the dialog and try again or contact DataEQ support.");
            }
        },

        triggerFetchProfiles() {
            this.$emit('fetch-linkedin-profiles', true);
        },

        setLoading(message, value) {
            this.$emit('loading-updated', message, value);
        },
    }


}
</script>

<style scoped lang="sass">


.linkedin-auth

    &__search-bar
        margin-bottom: 10px
        width: 90%

    &__profiles
        padding-right: 10px
        overflow-y: scroll

    .failed-login
        color: #df8505

    &__headers
        display: grid
        cursor: pointer
        overflow-x: auto
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr))
        position: sticky
        z-index: 10
        border: none
        color: var(--be-colour-text-dark)
        text-align: center

        span
            background: #222
            box-sizing: border-box
            border-top: 1px solid #1a1a1a
            border-right: 1px solid #1a1a1a
            padding: 5px 10px

            &:first-child
                border-top-left-radius: 3px
                border-left: 1px solid #1a1a1a

            &:last-child
                border-top-right-radius: 3px

    &__add-profile-container
        padding-right: 20px

    &__add-profile
        display: flex
        background: #444
        height: 90px
        border: 1px solid #272727
        padding: 10px
        margin-top: 20px
        margin-bottom: 30px
        border-radius: 6px

    &__enter-profile-details
        width: 50%

    &__found-profile
        width: 50%
        padding-left: 10px

    &__add-profile-divider
        margin: -10px 0
        border-right: 1px dashed #272727

    &__add-profile-loader
        margin-top: 0

    &__btn-bar
        display: flex
        align-items: center
        margin-top: 10px
        column-gap: 10px

        .login-status
            color: var(--be-colour-muted-text-dark)
            font-style: italic
            font-size: 0.9em
            padding-right: 10px
            margin-left: auto
            max-width: 295px

        .btn-linkedin
            ::v-deep .btn
                background: var(--colour-linkedin)

            &:hover
                ::v-deep .btn
                    filter: brightness(1.2)

    &__login-btn
        margin-top: 15px

        ::v-deep .btn
            background: var(--colour-linkedin)

        &:hover
            ::v-deep .btn
                filter: brightness(1.2)

    &__add-profile-button-bar
        display: flex
        margin-top: 50px
        column-gap: 10px

.slide-content-enter-active,
.slide-add-profile-enter-active
    transition: all .5s ease

.slide-content-leave-active,
.slide-add-profile-leave-active
    transition: none
    opacity: 0

.slide-content-enter
    transform: translateX(-5px)
    opacity: 0

.slide-add-profile-enter
    transform: translateX(5px)
    opacity: 0

</style>